import type { ComponentProps, ReactNode } from 'react';
import { cn } from '@/lib/cn';
import {
  controlBase,
  controlBorder,
  controlSizes,
  useFieldContext,
  type ControlSize,
} from './field-context';

export interface InputProps extends Omit<ComponentProps<'input'>, 'size'> {
  size?: ControlSize;
  invalid?: boolean;
  leftIcon?: ReactNode;
  rightSlot?: ReactNode;
}

export function Input({
  size = 'md',
  invalid,
  leftIcon,
  rightSlot,
  className,
  id,
  ...rest
}: InputProps) {
  const field = useFieldContext();
  const isInvalid = invalid ?? field?.invalid ?? false;
  const input = (
    <input
      id={id ?? field?.id}
      aria-invalid={isInvalid || undefined}
      aria-describedby={rest['aria-describedby'] ?? field?.describedBy}
      aria-required={field?.required || undefined}
      className={cn(
        controlBase,
        controlSizes[size],
        controlBorder(isInvalid),
        leftIcon && 'pl-9',
        rightSlot && 'pr-10',
        className,
      )}
      {...rest}
    />
  );
  if (!leftIcon && !rightSlot) return input;
  return (
    <div className="relative">
      {leftIcon && (
        <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-fg-subtle [&>svg]:size-4">
          {leftIcon}
        </span>
      )}
      {input}
      {rightSlot && (
        <span className="absolute inset-y-0 right-1 flex items-center">{rightSlot}</span>
      )}
    </div>
  );
}

export interface TextareaProps extends ComponentProps<'textarea'> {
  invalid?: boolean;
}

export function Textarea({ invalid, className, id, rows = 3, ...rest }: TextareaProps) {
  const field = useFieldContext();
  const isInvalid = invalid ?? field?.invalid ?? false;
  return (
    <textarea
      id={id ?? field?.id}
      rows={rows}
      aria-invalid={isInvalid || undefined}
      aria-describedby={rest['aria-describedby'] ?? field?.describedBy}
      className={cn(controlBase, 'px-3 py-2 text-sm', controlBorder(isInvalid), className)}
      {...rest}
    />
  );
}
